import { memo, useState, useEffect } from "react";
import { Container, Grid2, TextField, Typography, Box, Link, IconButton, InputAdornment } from "@mui/material";
import ArrowButton from "@/ui-kit/ArrowButton";
import { RESET_PASSWORD } from "@auth/graphql/mutations/resetPassword";
import { ApolloError, useMutation } from "@apollo/client";
import { validatePassword } from "@/utils/validateCredentials";
import { useDispatch } from "react-redux";
import { handleErrorWithNotification } from "@/utils/errorHandling";
import { triggerNotification } from "@store/slices/notification";
import { useSearchParams, useNavigate } from "react-router-dom";
import { icon } from "@/app/style/icons";
import { RouteTo } from "@/app/routing/RouteMap";

interface FormErrors {
    password?: string;
    confirmPassword?: string;
    submit?: string;
}

const ResetPasswordPage = memo(() => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSuccess, setIsSuccess] = useState(false);

    const [resetPasswordMutation, { loading }] = useMutation(RESET_PASSWORD);

    // Redirect if no token is provided
    useEffect(() => {
        if (!token) {
            dispatch(triggerNotification({
                content: "Ungültiger oder fehlender Reset-Token. Bitte fordern Sie einen neuen Link an.",
                type: "error",
                autoHideDuration: 5000
            }));
            navigate(RouteTo.FORGOT_PASSWORD);
        }
    }, [token, dispatch, navigate]);

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};
        
        if (!password) {
            newErrors.password = "Passwort ist erforderlich";
        } else {
            const passwordError = validatePassword(password);
            if (passwordError) {
                newErrors.password = passwordError;
            }
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = "Passwort-Bestätigung ist erforderlich";
        } else if (password !== confirmPassword) {
            newErrors.confirmPassword = "Passwörter stimmen nicht überein";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm() || !token) return;

        resetPasswordMutation({
            variables: { token, newPassword: password },
            onError: (error: ApolloError) => {
                if (error.message.includes('Invalid or expired')) {
                    setErrors({ submit: "Der Reset-Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen Link an." });
                } else {
                    handleErrorWithNotification(
                        error, 
                        dispatch, 
                        "Fehler beim Zurücksetzen des Passworts", 
                        "Es gab ein Problem beim Zurücksetzen Ihres Passworts. Bitte versuchen Sie es später erneut."
                    );
                }
            },
            onCompleted: () => {
                setIsSuccess(true);
                dispatch(triggerNotification({
                    content: "Ihr Passwort wurde erfolgreich zurückgesetzt. Sie können sich jetzt mit Ihrem neuen Passwort anmelden.",
                    type: "success",
                    autoHideDuration: 5000
                }));
            },
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            handleSubmit();
        }
    };

    if (!token) {
        return null; // Will redirect in useEffect
    }

    if (isSuccess) {
        return (
            <Container maxWidth="xs">
                <Grid2 container size={12} spacing={3} justifyContent="center">
                    <Grid2 mb={1}>
                        <Typography variant="h2">Passwort zurückgesetzt</Typography>
                    </Grid2>

                    <Grid2 size={12}>
                        <Typography variant="body1" textAlign="center">
                            Ihr Passwort wurde erfolgreich zurückgesetzt. Sie können sich jetzt mit Ihrem neuen Passwort anmelden.
                        </Typography>
                    </Grid2>

                    <Grid2 container spacing={2} justifyContent="center">
                        <Grid2>
                            <Link href="/auth/login">Zur Anmeldung</Link>
                        </Grid2>
                    </Grid2>
                </Grid2>
            </Container>
        );
    }

    return (
        <Container maxWidth="xs">
            <Grid2 container size={12} spacing={3} justifyContent="center">
                <Grid2 mb={1}>
                    <Typography variant="h2">Neues Passwort</Typography>
                </Grid2>

                <Grid2 size={12}>
                    <Typography variant="body1" textAlign="center" color="text.secondary">
                        Geben Sie Ihr neues Passwort ein.
                    </Typography>
                </Grid2>

                <Grid2 container size={12} spacing={2}>
                    <Grid2 size={12}>
                        <TextField
                            fullWidth
                            placeholder="Neues Passwort"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                setErrors((prev) => ({ ...prev, password: undefined, submit: undefined }));
                            }}
                            error={!!errors.password}
                            helperText={errors.password}
                            disabled={loading}
                            onKeyDown={handleKeyDown}
                            autoFocus
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={loading}>
                                            {showPassword ? (
                                                <icon.hidePassword sx={{ fontSize: 20, color: "semantic.icon.functional" }} />
                                            ) : (
                                                <icon.showPassword sx={{ fontSize: 20, color: "semantic.icon.functional" }} />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid2>

                    <Grid2 size={12}>
                        <TextField
                            fullWidth
                            placeholder="Passwort bestätigen"
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                setErrors((prev) => ({ ...prev, confirmPassword: undefined, submit: undefined }));
                            }}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            disabled={loading}
                            onKeyDown={handleKeyDown}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" disabled={loading}>
                                            {showConfirmPassword ? (
                                                <icon.hidePassword sx={{ fontSize: 20, color: "semantic.icon.functional" }} />
                                            ) : (
                                                <icon.showPassword sx={{ fontSize: 20, color: "semantic.icon.functional" }} />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid2>

                    {errors.submit && (
                        <Grid2 size={12}>
                            <Box sx={{ color: "semantic.notification.error", mt: 1 }}>{errors.submit}</Box>
                        </Grid2>
                    )}
                </Grid2>

                <Grid2 container spacing={2} justifyContent="center">
                    <Grid2>
                        <Link href={RouteTo.LOGIN} variant="nospace">Zurück zur Anmeldung</Link>
                    </Grid2>
                </Grid2>

                <Grid2>
                    <ArrowButton onClick={handleSubmit} disabled={loading || !password || !confirmPassword}>
                        {loading ? "Passwort wird zurückgesetzt..." : "Passwort zurücksetzen"}
                    </ArrowButton>
                </Grid2>
            </Grid2>
        </Container>
    );
});

export default ResetPasswordPage; 